import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { PieChart } from "@mui/x-charts/PieChart";
import PropTypes from "prop-types";
import * as React from "react";

const StyledText = styled("text", {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme }) => ({
  textAnchor: "middle",
  dominantBaseline: "central",
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: {
        variant: "primary",
      },
      style: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    {
      props: ({ variant }) => variant !== "primary",
      style: {
        fontSize: theme.typography.body2.fontSize,
      },
    },
    {
      props: {
        variant: "primary",
      },
      style: {
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== "primary",
      style: {
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <React.Fragment>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </React.Fragment>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

export default function AppPieChart(props) {
  const data = props.data || {};
  const colors = props.colors || [];
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <PieChart
          colors={colors}
          margin={{
            left: 80,
            right: 80,
            top: 80,
            bottom: 80,
          }}
          series={[
            {
              data,
              innerRadius: 75,
              outerRadius: 100,
              paddingAngle: 0,
              highlightScope: { faded: "global", highlighted: "item" },
            },
          ]}
          height={260}
          width={260}
          slotProps={{
            legend: { hidden: true },
          }}
        >
          <PieCenterLabel
            primaryText={data.reduce((acc, c) => acc + c.value, 0)}
            secondaryText="Total"
          />
        </PieChart>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {data.map((item, index) => (
            <Stack
              key={index}
              direction="row"
              sx={{ alignItems: "center", gap: 2 }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: colors[index % colors.length],
                  borderRadius: "50%",
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: "500" }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {item.value}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Box>
    </>
  );
}
